/**
 * Admin "Create Challenge" modal — extracted from `ChallengesPage.js` so
 * that page stays under the complexity ceiling. Purely presentational:
 * receives the draft in `value`, emits changes through `onChange`, and
 * calls `onSubmit` when the admin hits Create.
 */
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, X, Loader2, Trophy } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

// Style shared by every SelectItem inside the challenge-type dropdown.
// Extracted to a constant so the JSX stays readable.
const SELECT_ITEM_CLASSES =
  'font-heading uppercase tracking-wider text-sm cursor-pointer text-white ' +
  'hover:!bg-primary/20 focus:!bg-primary/20 focus:!text-primary ' +
  'data-[state=checked]:!text-primary data-[state=checked]:!bg-primary/10 ' +
  '[&>span:first-child]:text-primary';

const CHALLENGE_TYPES = [
  ['workout_count', 'Complete Workouts'],
  ['total_minutes', 'Total Minutes'],
  ['total_calories', 'Burn Calories'],
  ['streak', 'Day Streak'],
  ['daily_reps', 'Daily Reps (e.g., Push-ups)'],
  ['daily_water', 'Daily Hydration (oz)'],
];

export const CreateChallengeModal = ({ open, onClose, value, onChange, onSubmit, creating }) => {
  const patch = (partial) => onChange({ ...value, ...partial });

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-card border border-border p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <h3 className="font-heading text-xl text-white uppercase tracking-wider">
                  Create Challenge
                </h3>
              </div>
              <button onClick={onClose} className="text-muted-foreground hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-muted-foreground text-xs uppercase">Challenge Title</Label>
                <Input
                  value={value.title}
                  onChange={(e) => patch({ title: e.target.value })}
                  placeholder="e.g., 30-Day Push-Up Challenge"
                  className="mt-1 bg-black border-border text-white"
                />
              </div>

              <div>
                <Label className="text-muted-foreground text-xs uppercase">Description</Label>
                <Textarea
                  value={value.description}
                  onChange={(e) => patch({ description: e.target.value })}
                  placeholder="Describe what participants need to do..."
                  className="mt-1 bg-black border-border text-white"
                  rows={3}
                />
              </div>

              <div>
                <Label className="text-muted-foreground text-xs uppercase">Challenge Type</Label>
                <Select value={value.type} onValueChange={(v) => patch({ type: v })}>
                  <SelectTrigger
                    data-testid="create-challenge-type-select"
                    className="mt-1 h-11 bg-black border border-primary/30 text-white font-heading uppercase tracking-wider text-sm px-3 hover:border-primary hover:bg-black/60 focus:ring-2 focus:ring-primary/60 focus:border-primary transition-colors data-[state=open]:border-primary data-[state=open]:ring-2 data-[state=open]:ring-primary/60"
                  >
                    <SelectValue placeholder="Select challenge type" />
                  </SelectTrigger>
                  <SelectContent
                    className="bg-zinc-950 border border-primary/40 text-white shadow-2xl shadow-primary/20"
                    position="popper"
                  >
                    {CHALLENGE_TYPES.map(([k, label]) => (
                      <SelectItem key={k} value={k} className={SELECT_ITEM_CLASSES}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground text-xs uppercase">Target Goal</Label>
                  <Input
                    type="number"
                    value={value.target}
                    onChange={(e) => patch({ target: parseInt(e.target.value, 10) || 0 })}
                    className="mt-1 bg-black border-border text-white"
                    min="1"
                  />
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs uppercase">Duration (Days)</Label>
                  <Input
                    type="number"
                    value={value.duration_days}
                    onChange={(e) => patch({ duration_days: parseInt(e.target.value, 10) || 7 })}
                    className="mt-1 bg-black border-border text-white"
                    min="1"
                    max="365"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_team"
                  checked={value.is_team_challenge}
                  onChange={(e) => patch({ is_team_challenge: e.target.checked })}
                  className="w-4 h-4 accent-primary"
                />
                <Label htmlFor="is_team" className="text-white cursor-pointer">
                  Team Challenge (participants form teams)
                </Label>
              </div>

              <Button
                onClick={onSubmit}
                disabled={creating}
                className="w-full bg-primary text-black hover:bg-primary/90 mt-4"
              >
                {creating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Trophy className="h-4 w-4 mr-2" />
                    Create Challenge
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CreateChallengeModal;
